import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Plus, Check, Clock3, ShoppingBag, ArrowRight, Trash2, ChevronDown } from "lucide-react";
import { CATEGORIES, ALL_ITEMS } from "@/data/catalog";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function TestCatalog({ selected, setSelected, onOpenCart, initialQuery }) {
  const [query, setQuery] = useState(initialQuery || "");
  const [openCats, setOpenCats] = useState(["hematopathology"]);

  useEffect(() => { if (initialQuery !== undefined) setQuery(initialQuery); }, [initialQuery]);

  const searching = query.trim().length > 0;
  const items = useMemo(() => {
    if (!searching) return [];
    const q = query.toLowerCase();
    return ALL_ITEMS.filter((i) => i.name.toLowerCase().includes(q));
  }, [query, searching]);

  const cats = CATEGORIES.filter((c) => c.id !== "all");
  const toggleCat = (id) => setOpenCats((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const total = selected.reduce((s, id) => s + (ALL_ITEMS.find((i) => i.id === id)?.price || 0), 0);

  return (
    <section id="tests" className="bg-white py-20 lg:py-28" data-testid="tests-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="flex items-center gap-4 mb-6">
          <span className="font-mono-med text-sm text-[#D92638] border border-[#D92638]/40 rounded-full px-3.5 py-1">04</span>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500">Test catalog</span>
          <span className="h-px flex-1 bg-slate-200" />
        </motion.div>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-2xl leading-tight">
            Transparent, competitive pricing. Pick your tests, pay by UPI.
          </motion.h2>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="relative w-full lg:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tests - CBC, thyroid, dengue..."
              data-testid="test-search-input"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 bg-[#F8FAFC] text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488] transition-all" />
          </motion.div>
        </div>

        {searching ? (
          <>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="test-search-results">
              {items.map((item) => <TestCard key={item.id} item={item} inCart={selected.includes(item.id)} onToggle={toggle} />)}
            </div>
            {items.length === 0 && (
              <div className="text-center py-16 text-slate-500" data-testid="tests-empty">No tests match your search. Try "thyroid" or "CBC".</div>
            )}
          </>
        ) : (
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
            className="mt-8 space-y-4" data-testid="test-category-accordions">
            {cats.map((c) => {
              const catItems = ALL_ITEMS.filter((i) => i.category === c.id);
              const isOpen = openCats.includes(c.id);
              const minPrice = Math.min(...catItems.map((i) => i.price));
              return (
                <div key={c.id} className="rounded-2xl border border-slate-200/90 overflow-hidden bg-white shadow-[0_4px_20px_-4px_rgba(15,25,44,0.06)]">
                  <button onClick={() => toggleCat(c.id)} data-testid={`test-category-tab-${c.id}`}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 hover:bg-[#F8FAFC] transition-colors">
                    <div className="flex items-center gap-3.5">
                      <span className="font-bold text-[#0B192C] text-base">{c.label}</span>
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">{catItems.length} tests</span>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <span className="font-mono-med text-xs text-slate-500 hidden sm:block">from ₹{minPrice.toLocaleString("en-IN")}</span>
                      <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 px-5 pb-5">
                          {catItems.map((item) => <TestCard key={item.id} item={item} inCart={selected.includes(item.id)} onToggle={toggle} />)}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* Floating cart bar */}
      <AnimatePresence>
        {selected.length > 0 && (
          <motion.div initial={{ y: 90, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 90, opacity: 0 }}
            className="fixed bottom-5 left-4 right-4 sm:right-auto z-40 sm:min-w-[440px]"
            data-testid="booking-cart-bar">
            <div className="bg-[#0B192C]/95 backdrop-blur-xl border border-white/15 rounded-2xl shadow-[0_20px_60px_-10px_rgba(11,25,44,0.6)] px-5 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[#D92638]/20 flex items-center justify-center shrink-0">
                  <ShoppingBag className="w-5 h-5 text-[#D92638]" />
                </div>
                <div className="min-w-0">
                  <div className="text-white text-sm font-semibold truncate">{selected.length} item{selected.length > 1 ? "s" : ""} selected</div>
                  <div className="font-mono-med text-[#14B8A6] text-sm">₹{total.toLocaleString("en-IN")} total</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setSelected([])} aria-label="Clear selection" data-testid="cart-clear-button"
                  className="w-10 h-10 rounded-xl border border-white/20 flex items-center justify-center text-slate-300 hover:text-white hover:border-white/40 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={onOpenCart} data-testid="cart-book-button"
                  className="flex items-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 active:scale-95">
                  View cart <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

function TestCard({ item, inCart, onToggle }) {
  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.25 }}
      data-testid={`test-card-${item.id}`}
      className={`group bg-[#F8FAFC] rounded-2xl border p-5 flex flex-col hover:-translate-y-1 transition-all duration-300 hover:shadow-[0_12px_32px_-6px_rgba(15,25,44,0.12)] ${inCart ? "border-[#0D9488] ring-1 ring-[#0D9488]/30 bg-[#0D9488]/[0.04]" : "border-slate-200/90"}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-[#0B192C] text-[15px] leading-snug">{item.name}</h3>
        <button onClick={() => onToggle(item.id)} data-testid={`test-add-${item.id}`} aria-label={inCart ? "Remove" : "Add"}
          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 active:scale-90 ${inCart ? "bg-[#0D9488] text-white" : "bg-white border border-slate-300 text-slate-500 group-hover:border-[#0D9488] group-hover:text-[#0D9488]"}`}>
          {inCart ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </button>
      </div>
      <p className="mt-2 text-xs text-slate-500 leading-relaxed flex-1">{item.desc}</p>
      <div className="mt-4 flex items-center justify-between">
        <span className="font-mono-med text-lg font-bold text-[#0B192C]">₹{item.price.toLocaleString("en-IN")}</span>
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
          <Clock3 className="w-3.5 h-3.5 text-[#D92638]" /> {item.tat}
        </span>
      </div>
    </motion.div>
  );
}
